import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import MenuShell from '../components/MenuShell';
import type { BadgeCode, BadgeRule } from '../features/badges/badgeApi';
import { deleteBadgeRule, getBadgeRules, putBadgeRule } from '../features/badges/badgeApi';
import {
  getCourse,
  listChapters,
  listExercisesByChapter,
} from '../features/instructor/instructorApi';
import type { ExerciseAuthoring } from '../features/instructor/instructorApi';
import './InstructorBadgeRulesPage.css';

const BADGE_CODES: BadgeCode[] = ['B-01', 'B-02', 'B-03', 'B-04', 'B-05', 'B-06'];

function badgeName(
  code: BadgeCode,
  t: (key: string, options?: { defaultValue?: string }) => string,
): string {
  return t(`badges.catalog.${code}.name`, { defaultValue: code });
}

function readErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function BadgeRuleRow({
  badgeCode,
  rule,
  assignedTitle,
  publishedExercises,
  selectedExerciseId,
  busy,
  saving,
  onExerciseChange,
  onSave,
  onRemove,
}: {
  badgeCode: BadgeCode;
  rule: BadgeRule | undefined;
  assignedTitle: string | undefined;
  publishedExercises: ExerciseAuthoring[];
  selectedExerciseId: string;
  busy: boolean;
  saving: boolean;
  onExerciseChange: (exerciseId: string) => void;
  onSave: () => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const name = badgeName(badgeCode, t);

  return (
    <section className="badge-rules-row">
      <div className="badge-rules-row__badge">
        <img src={`/assets/badges/${badgeCode.toLowerCase()}.svg`} alt="" aria-hidden="true" />
        <div>
          <h2>{name}</h2>
          {assignedTitle ? <p>{assignedTitle}</p> : <p>{t('badges.noRule')}</p>}
        </div>
      </div>
      <label htmlFor={`exercise-${badgeCode}`}>{t('badges.ruleExerciseLabel', { name })}</label>
      <div className="badge-rules-row__controls">
        <select
          id={`exercise-${badgeCode}`}
          value={selectedExerciseId}
          disabled={busy}
          onChange={(event) => onExerciseChange(event.target.value)}
        >
          <option value="">{t('badges.chooseExercise')}</option>
          {publishedExercises.map((exercise) => (
            <option key={exercise.id} value={exercise.id}>
              {exercise.title}
            </option>
          ))}
        </select>
        <button type="button" disabled={busy || !selectedExerciseId} onClick={onSave}>
          {saving ? t('badges.saving') : t('badges.saveRule', { name })}
        </button>
        {rule?.exercise ? (
          <button
            type="button"
            className="badge-rules-row__remove"
            disabled={busy}
            onClick={onRemove}
          >
            {t('badges.removeRule', { name })}
          </button>
        ) : null}
      </div>
    </section>
  );
}

function InstructorBadgeRulesPage() {
  const { t } = useTranslation();
  const { courseId = '' } = useParams();
  const [courseTitle, setCourseTitle] = useState('');
  const [rules, setRules] = useState<BadgeRule[]>([]);
  const [publishedExercises, setPublishedExercises] = useState<ExerciseAuthoring[]>([]);
  const [selectedExercises, setSelectedExercises] = useState<Record<string, string>>({});
  const [busyCode, setBusyCode] = useState<BadgeCode | null>(null);
  const [loading, setLoading] = useState(true);
  const [configurationLoaded, setConfigurationLoaded] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;

    async function loadConfiguration() {
      try {
        setLoading(true);
        setConfigurationLoaded(false);
        setErrorMessage(null);
        const [course, initialRules, chapters] = await Promise.all([
          getCourse(courseId),
          getBadgeRules(courseId),
          listChapters(courseId),
        ]);
        const exercisesByChapter = await Promise.all(
          chapters.map((chapter) => listExercisesByChapter(chapter.id)),
        );
        if (ignore) {
          return;
        }
        setCourseTitle(course.title);
        setRules(initialRules);
        setPublishedExercises(
          exercisesByChapter.flat().filter((exercise) => exercise.status === 'PUBLISHED'),
        );
        setSelectedExercises(
          Object.fromEntries(
            initialRules
              .filter((rule) => rule.exercise)
              .map((rule) => [rule.badgeCode, rule.exercise?.id ?? '']),
          ),
        );
        setConfigurationLoaded(true);
      } catch (error) {
        if (!ignore) {
          setErrorMessage(readErrorMessage(error, t('badges.rulesLoadFailed')));
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    void loadConfiguration();
    return () => {
      ignore = true;
    };
  }, [courseId, t]);

  const exerciseTitles = useMemo(
    () => new Map(publishedExercises.map((exercise) => [exercise.id, exercise.title])),
    [publishedExercises],
  );

  async function saveRule(badgeCode: BadgeCode) {
    const exerciseId = selectedExercises[badgeCode];
    if (!exerciseId) {
      return;
    }
    setBusyCode(badgeCode);
    setErrorMessage(null);
    setStatusMessage(null);
    try {
      const savedRule = await putBadgeRule(courseId, badgeCode, exerciseId);
      const exercise = savedRule.exercise ?? {
        id: exerciseId,
        title: exerciseTitles.get(exerciseId) ?? '',
      };
      setRules((current) => [
        ...current.filter((rule) => rule.badgeCode !== badgeCode),
        { ...savedRule, exercise },
      ]);
      setStatusMessage(t('badges.ruleSaved'));
    } catch (error) {
      setErrorMessage(readErrorMessage(error, t('badges.ruleSaveFailed')));
    } finally {
      setBusyCode(null);
    }
  }

  async function removeRule(badgeCode: BadgeCode) {
    setBusyCode(badgeCode);
    setErrorMessage(null);
    setStatusMessage(null);
    try {
      await deleteBadgeRule(courseId, badgeCode);
      setRules((current) =>
        current.map((rule) => (rule.badgeCode === badgeCode ? { ...rule, exercise: null } : rule)),
      );
      setSelectedExercises((current) => ({ ...current, [badgeCode]: '' }));
      setStatusMessage(t('badges.ruleRemoved'));
    } catch (error) {
      setErrorMessage(readErrorMessage(error, t('badges.ruleRemoveFailed')));
    } finally {
      setBusyCode(null);
    }
  }

  const rulesByCode = new Map(rules.map((rule) => [rule.badgeCode, rule]));

  return (
    <>
      <MenuShell courseId={courseId} />
      <main className="badge-rules-page">
        <header>
          <Link to={`/courses/${courseId}`}>{t('coursePage.backHome')}</Link>
          <h1>{t('badges.rulesTitle')}</h1>
          {courseTitle ? <p>{courseTitle}</p> : null}
          <p>{t('badges.rulesDescription')}</p>
        </header>

        {loading ? <p role="status">{t('badges.rulesLoading')}</p> : null}
        {errorMessage ? <p role="alert">{errorMessage}</p> : null}
        {statusMessage ? <p role="status">{statusMessage}</p> : null}

        {!loading && configurationLoaded ? (
          <>
            {publishedExercises.length === 0 ? <p>{t('badges.noPublishedExercises')}</p> : null}
            <div className="badge-rules-list">
              {BADGE_CODES.map((badgeCode) => {
                const currentRule = rulesByCode.get(badgeCode);
                const assignedTitle =
                  currentRule?.exercise?.title ??
                  (currentRule?.exercise ? exerciseTitles.get(currentRule.exercise.id) : undefined);
                return (
                  <BadgeRuleRow
                    key={badgeCode}
                    badgeCode={badgeCode}
                    rule={currentRule}
                    assignedTitle={assignedTitle}
                    publishedExercises={publishedExercises}
                    selectedExerciseId={
                      selectedExercises[badgeCode] ?? currentRule?.exercise?.id ?? ''
                    }
                    busy={busyCode !== null}
                    saving={busyCode === badgeCode}
                    onExerciseChange={(exerciseId) =>
                      setSelectedExercises((current) => ({
                        ...current,
                        [badgeCode]: exerciseId,
                      }))
                    }
                    onSave={() => void saveRule(badgeCode)}
                    onRemove={() => void removeRule(badgeCode)}
                  />
                );
              })}
            </div>
          </>
        ) : null}
      </main>
    </>
  );
}

export default InstructorBadgeRulesPage;
